import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPatch, apiPost } from '../../lib/api';

export const adminClientsKey = (params) => ['admin', 'clients', params || {}];
export const adminClientKey = (id) => ['admin', 'clients', id];

export function useAdminClients(params = {}) {
  const q = new URLSearchParams();
  if (params.q) q.set('q', params.q);
  if (params.status) q.set('status', params.status);
  if (params.page) q.set('page', String(params.page));
  if (params.page_size) q.set('page_size', String(params.page_size));
  const qs = q.toString();
  return useQuery({
    queryKey: adminClientsKey(params),
    queryFn: () => apiGet(`/admin/clients${qs ? `?${qs}` : ''}`),
  });
}

export function useAdminClient(id) {
  return useQuery({
    queryKey: adminClientKey(id),
    queryFn: () => apiGet(`/admin/clients/${id}`),
    enabled: Boolean(id),
  });
}

export function useOnboardClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => apiPost('/admin/clients/onboard', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'clients'] }),
  });
}

export function useUpdateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }) => apiPatch(`/admin/clients/${id}`, body),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['admin', 'clients'] });
      qc.invalidateQueries({ queryKey: adminClientKey(vars.id) });
    },
  });
}

export function useSuspendClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => apiPost(`/admin/clients/${id}/suspend`),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['admin', 'clients'] });
      qc.invalidateQueries({ queryKey: adminClientKey(id) });
    },
  });
}

export function useActivateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => apiPost(`/admin/clients/${id}/activate`),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['admin', 'clients'] });
      qc.invalidateQueries({ queryKey: adminClientKey(id) });
    },
  });
}

export function useRenewClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, new_end_date }) => apiPost(`/admin/clients/${id}/renew`, { new_end_date }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['admin', 'clients'] });
      qc.invalidateQueries({ queryKey: adminClientKey(vars.id) });
    },
  });
}

export function useResetAdminPassword() {
  return useMutation({
    mutationFn: ({ clientId, userId, password }) =>
      apiPost(`/admin/clients/${clientId}/admins/${userId}/reset-password`, {
        new_temporary_password: password,
      }),
  });
}

export function useAssignProjects() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, project_ids }) => apiPost(`/admin/clients/${id}/projects`, { project_ids }),
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: adminClientKey(vars.id) }),
  });
}
